import { defineStore } from "pinia";
import moment from "moment";
import { ElMessageBox } from "element-plus";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { Comment } from "~/types/comment";
import { Profile } from "~/types/profile";
import {
  Order,
  OrderStage,
  OrderByStage,
} from "~/projects/order-manager/types";
import { OrderView } from "~/projects/order-manager/types/enums/OrderView";
import {
  OrderCountSummaryReport,
  OrderCountSummaryReportFilterData,
} from "~/projects/order-manager/types/orderCountSummaryReport";
import { useConfigStore } from "~/stores/configStore";
import { useProductStore } from "~/stores/productStore";
import { DeliveryStatus } from "~/projects/order-manager/types/enums/DeliveryStatus";

const sanitizeForFileName = (value: unknown) =>
  String(value)
    .trim()
    .replace(/[^\w-]+/g, "-");

export const useOrdersStore = defineStore("ordersStore", {
  state: () => ({
    _view: OrderView.BOARD,
    _tableData: [] as Array<Order>,
    _selectedOrderForEdit: {} as Order,
    multipleSelection: [] as Array<Order>,
    _filterData: {},
    /*
     * Counts behind each status tab and partner sub-tab.
     *
     * Held in the store rather than the page because three different places
     * refetch the list — the tab strip, the filter panel's Apply and its Reset
     * — and two of them call the store directly. Refreshing alongside the rows
     * here means no caller can forget, and the numbers cannot describe a
     * different query than the table under them.
     *
     * null, not 0, until the first answer arrives: a tab showing "0" that
     * becomes "47" a moment later is worse than one showing nothing yet.
     */
    _statusCounts: null as Record<string, number> | null,
    _partnerCounts: null as Record<string, number> | null,
    _partnerTotal: null as number | null,
    _submittedFilterData: {},
    _pagination: {} as Pagination,
    _order: {} as Order,
    createOrderStage: "" as string,
    _stages: [] as Array<OrderStage>,
    tableLoader: true,
    showOrderEditModal: false,
    showBulkUpload: false,
    showListEditDialog: false,
    showListCreateDialog: false,
    showOrderCreateModal: false,
    _orderStage: [] as OrderByStage[],
    _orderComments: [] as Comment[],
    _selectedProfileForOrder: {} as Profile,
    _orderableData: null as null | Pick<Order, "source_id" | "source_type">,
    showInvoiceDialog: false,
    invoiceAutoPrint: false,
    _selectedOrderForInvoicePreview: null as Order | null,
    _selectedOrdersForInvoicePreview: [] as Array<Order>,
    _orderCountSummaryReport: null as OrderCountSummaryReport | null,
    // Sent orders by default, like the product order report; the filter can
    // widen it to drafts or to every status.
    _orderCountSummaryReportFilter: {
      group_by: "daily",
      from_date: moment().subtract(14, "days").format("YYYY-MM-DD"),
      to_date: moment().format("YYYY-MM-DD"),
      order_status: "sent_orders",
    } as OrderCountSummaryReportFilterData,
    orderCountSummaryReportLoader: false,
    // Product rows per page; the API caps this at 50.
    orderCountSummaryReportPerPage: 50,
    // Cached delivery-partner integrations (fetched once and shared, so
    // per-message components like OutgoingMsg don't each hit the API).
    _deliveryPartnerIntegrations: [] as any[],
    _deliveryPartnerIntegrationsLoaded: false,
    _deliveryPartnerIntegrationsPromise: null as Promise<any[]> | null,
  }),
  getters: {
    showEditOptions(state) {
      return state.multipleSelection.length > 0;
    },
    tableData: (state) => state._tableData,
    selectedOrderForEdit: (state) => state._selectedOrderForEdit,
    pagination: (state) => state._pagination,
    stages: (state) => state._stages,
    order: (state) => state._order,
    comments: (state) => state._orderComments,
    orderStage: (state) => state._orderStage,
    view: (state) => state._view,
    selectedProfileForOrder: (state) => state._selectedProfileForOrder,
    orderableData: (state) => state._orderableData,
    selectedOrderForInvoicePreview: (state) =>
      state._selectedOrderForInvoicePreview,
    selectedOrdersForInvoicePreview: (state) =>
      state._selectedOrdersForInvoicePreview || [],
    orderCountSummaryReport: (state) => state._orderCountSummaryReport,
    deliveryPartnerIntegrations: (state) => state._deliveryPartnerIntegrations,
    deliveryPartnerIntegrationsLoaded: (state) =>
      state._deliveryPartnerIntegrationsLoaded,
  },
  actions: {
    // Unified Delivery Partner Integration APIs
    async createDeliveryPartnerIntegration(data: any) {
      const { response, error } = await postRequest(
        "order-manager/delivery-partners",
        data,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getDeliveryPartnerIntegrations(type?: string) {
      let url = "order-manager/delivery-partners";
      if (type) {
        url += `?type=${type}`;
      }
      const { response, error } = await getRequest(url);
      if (!error) {
        return response.data;
      }
      return null;
    },
    // Fetch the org's delivery-partner integrations once and cache them, so
    // per-message components (OutgoingMsg) don't each hit the API. Concurrent
    // callers share the in-flight request.
    async loadDeliveryPartnerIntegrations() {
      if (this._deliveryPartnerIntegrationsLoaded) {
        return this._deliveryPartnerIntegrations;
      }
      if (this._deliveryPartnerIntegrationsPromise) {
        return this._deliveryPartnerIntegrationsPromise;
      }
      this._deliveryPartnerIntegrationsPromise = (async () => {
        const data = await this.getDeliveryPartnerIntegrations();
        this._deliveryPartnerIntegrations = Array.isArray(data) ? data : [];
        this._deliveryPartnerIntegrationsLoaded = true;
        this._deliveryPartnerIntegrationsPromise = null;
        return this._deliveryPartnerIntegrations;
      })();
      return this._deliveryPartnerIntegrationsPromise;
    },
    async updateDeliveryPartnerIntegration(id: number, data: any) {
      const { response, error } = await putRequest(
        `order-manager/delivery-partners/${id}`,
        data,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deleteDeliveryPartnerIntegration(id: number) {
      return await deleteRequest(`order-manager/delivery-partners/${id}`);
    },
    async makeDeliveryPartnerDefault(id: number) {
      const { response, error } = await putRequest(
        `order-manager/delivery-partners/${id}/set-default`,
        {},
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async generateDeliveryPartnerWebhookCredentials(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/webhook-credentials`,
        {},
      );
      return response;
    },
    // Steadfast Integration APIs (DEPRECATED - use unified APIs above)
    async createOrUpdateSteadfastIntegration(data: any) {
      const { response, error } = await postRequest(
        "order-manager/steadfast/integration",
        data,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getSteadfastIntegrations() {
      const { response, error } = await getRequest(
        "order-manager/steadfast/integration",
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deleteSteadfastIntegration(name: string) {
      const { response, error } = await deleteRequest(
        "order-manager/steadfast/integration",
        { name },
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    // Steadfast Order APIs
    async createSteadfastOrder(orderData: any) {
      const response = await postRequest(
        "order-manager/steadfast/create-order",
        orderData,
      );
      return response;
    },
    async trackSteadfastOrder(trackingCode: string, storeName?: string) {
      let url = `order-manager/steadfast/orders/${trackingCode}`;
      if (storeName) url += `?store_name=${encodeURIComponent(storeName)}`;
      const { response, error } = await getRequest(url);
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getPathaoStores() {
      const { response, error } = await getRequest(
        "order-manager/pathao/stores",
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getOrders(pageNo = 1, sortBy = "id", ascending = false) {
      this.tableLoader = true;

      // Counts are per query, not per page, so paging does not refetch them.
      if (pageNo === 1) {
        void this.refreshStatusCounts();
      }

      const sortDir = ascending ? "asc" : "desc";
      let url = `order-manager/orders?pagination=true&page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const key in this._filterData) {
        // @ts-ignore
        url += `&${key}=${this._filterData[key]}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Order>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this.$state._pagination = pagination;
        this.tableLoader = false;
      }
    },
    // Pathao Integration APIs
    async createOrUpdatePathaoIntegration(data: any) {
      const { response, error } = await postRequest(
        "order-manager/pathao/integration",
        data,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getPathaoIntegration() {
      const { response, error } = await getRequest(
        "order-manager/pathao/integration",
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deletePathaoIntegration() {
      const result = await deleteRequest("order-manager/pathao/integration");
      if (result && result.data) {
        return result.data;
      }
      return null;
    },
    // Pathao Order APIs
    async getPathaoCities() {
      const { response, error } = await getRequest(
        "order-manager/pathao/cities",
      );
      if (!error) {
        console.log(response.data);
        return response.data;
      }
      return null;
    },
    async getPathaoZones(cityId: number) {
      const { response, error } = await getRequest(
        `order-manager/pathao/zones/${cityId}`,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getPathaoAreas(zoneId: number) {
      const { response, error } = await getRequest(
        `order-manager/pathao/areas/${zoneId}`,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async createPathaoOrder(orderData: any) {
      const response = await postRequest(
        "order-manager/pathao/create-order",
        orderData,
      );

      return response;
    },
    async createDraftOrder(orderData: any) {
      const response = await postRequest(
        "order-manager/orders/draft",
        orderData,
      );

      return response;
    },
    /**
     * Ask the server to tell the store again about something that did not land.
     *
     * The action is named rather than left to the server to work out. A
     * fulfilment that failed while the order has since moved on must retry the
     * fulfilment, not whatever the current status would imply.
     */
    async retryStorePush(orderId: number, action: string) {
      const response = await postRequest(
        `order-manager/orders/${orderId}/retry-store-push`,
        { action },
      );

      return response;
    },
    async updateDraftOrder(orderId: number, orderData: any) {
      const response = await putRequest(
        `order-manager/orders/${orderId}/draft`,
        orderData,
      );

      return response;
    },
    async submitOrderToDeliveryPartner(orderId: number) {
      const response = await postRequest(
        `order-manager/orders/${orderId}/submit-to-delivery-partner`,
        {},
      );

      return response;
    },
    async trackPathaoOrder(externalOrderId: string) {
      const { response, error } = await getRequest(
        `order-manager/pathao/orders/${externalOrderId}`,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async exportTable() {
      let url = `order-manager/orders/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"orders-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    async exportOrders() {
      await this.exportTable();
    },
    // Order Count Summary Report APIs
    buildOrderCountSummaryReportQuery() {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(
        this._orderCountSummaryReportFilter,
      )) {
        if (value === null || value === undefined || value === "") continue;
        if (Array.isArray(value)) {
          if (value.length) params.append(key, value.join(","));
          continue;
        }
        params.append(key, String(value));
      }
      return params.toString();
    },
    // Paged over the product rows; the period columns always come in full.
    async getOrderCountSummaryReport(page = 1) {
      this.orderCountSummaryReportLoader = true;
      const url = `order-manager/reports/order-count-summary?${this.buildOrderCountSummaryReportQuery()}&page=${page}&per_page=${
        this.orderCountSummaryReportPerPage
      }`;

      const { response, error } = await getRequest<OrderCountSummaryReport>(
        url,
      );
      this.orderCountSummaryReportLoader = false;

      if (error) {
        ElMessageBox({
          title: "Error Fetching reports",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        return null;
      }

      this.$state._orderCountSummaryReport = response.data;
      return response.data;
    },
    async exportOrderCountSummaryReport() {
      const url = `order-manager/reports/order-count-summary/export?${this.buildOrderCountSummaryReportQuery()}`;

      const filter = this._orderCountSummaryReportFilter;
      let fileName = `order-count-summary-${filter.group_by}-${filter.from_date}-to-${filter.to_date}`;

      if (filter.agent_id) {
        const agent = useConfigStore().globalConfig.agents?.find(
          (item: any) => item.id === Number(filter.agent_id),
        );
        fileName += `-agent-${sanitizeForFileName(
          agent?.name ?? filter.agent_id,
        )}`;
      }

      const productIds = filter.product_ids ?? [];
      if (productIds.length) {
        const names = useProductStore()
          .allProducts.filter((product: any) => productIds.includes(product.id))
          .map((product: any) => sanitizeForFileName(product.name));

        fileName += `-products-${(names.length ? names : productIds).join(
          "-",
        )}`;
      }

      await downloadWithSavePicker(url, `${fileName}.xlsx`);
    },
    setOrderCountSummaryReportFilter(
      filter: OrderCountSummaryReportFilterData,
    ) {
      this.$state._orderCountSummaryReportFilter = filter;
    },
    async createOrder(order: any) {
      // Returns the raw { response, error } so callers (CreateForm) can consume
      // it the same way as createDraftOrder.
      return await postRequest("order-manager/orders", order);
    },
    async confirmOrder(orderId: number) {
      return await postRequest(`order-manager/orders/${orderId}/confirm`, {});
    },
    async updateOrder(order: Order) {
      const { response, error } = await putRequest<Order>(
        `order-manager/orders/${order.id}`,
        order,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deleteOrder(order: Order) {
      return await deleteRequest(`order-manager/orders/${order.id}`);
    },
    async getOrder(orderId: number) {
      const { response, error } = await getRequest<Order>(
        `order-manager/orders/${orderId}`,
      );
      if (!error) {
        this.$state._order = response.data;
      }
    },
    async getOrderComments(data: {
      commentable_id: number;
      commentable_type: string;
    }) {
      const params = new URLSearchParams({
        commentable_id: data.commentable_id.toString(),
        commentable_type: data.commentable_type,
      });

      const url = `comments?${params.toString()}`;

      const { response, error } = await getRequest<Comment[]>(url);

      if (!error) {
        this._orderComments = response.data;
      }
    },
    async moveOrder(orderData: Partial<Order>) {
      const { response, error } = await postRequest<Order>(
        `order-manager/orders/${orderData.id}/status`,
        orderData,
      );
      if (!error) {
        return response.data;
      }
      return error;
    },
    /**
     * Which statuses this order can actually be moved to.
     *
     * Asked of the server rather than worked out here. Karbari, EcomDrive,
     * Nuport and BizMotion take the order over at submission and report
     * nothing back, so an order on them never moves past Sent — and that rule
     * lives in one service on the backend which also guards the write. Two
     * lists that happen to agree today would drift; an option the server then
     * refuses is worse than a missing one, because the agent only finds out
     * after pressing it.
     */
    async getReachableStatuses(orderId: number) {
      const { response, error } = await getRequest<{
        statuses: Array<{ value: number; label: string }>;
      }>(`order-manager/orders/${orderId}/reachable-statuses`);

      if (error) return null;
      return response.data.statuses ?? [];
    },
    /**
     * Set an order's delivery status by hand.
     *
     * Needed because a courier's signal is sometimes wrong, sometimes late and
     * sometimes absent — Steadfast never reports a return at all, so without
     * this nobody could record one. The stock consequence is decided on the
     * server from the status, not here.
     */
    /**
     * Counts for the status tabs and the partner sub-tabs beneath them.
     *
     * Fetched from the server rather than counted from the loaded rows, which
     * would only ever count the current page. Takes the same filters as the
     * list so the numbers agree with what is underneath them.
     */
    async refreshStatusCounts() {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null || value === undefined || value === "") continue;
        params.append(key, String(value));
      }

      const query = params.toString();
      const { response, error } = await getRequest<{
        status_counts: Record<string, number>;
        partner_counts: Record<string, number>;
        total: number;
      }>(`order-manager/orders/status-counts${query ? `?${query}` : ""}`);

      // A failed count must not blank the tabs or block the list. The previous
      // numbers stay until a good answer replaces them.
      if (error) return;

      this.$state._statusCounts = response.data.status_counts ?? {};
      this.$state._partnerCounts = response.data.partner_counts ?? {};
      this.$state._partnerTotal = response.data.total ?? 0;
    },
    async setStatus(orderId: number, status: DeliveryStatus, remarks?: string) {
      // Not `/status` — that path belongs to the kanban board, which posts
      // {stage_id, position} to it. Two different meanings of the word.
      return await postRequest<Order>(
        `order-manager/orders/${orderId}/delivery-status`,
        { status, remarks },
      );
    },
    /**
     * Record that a returned parcel physically arrived, which is the only thing
     * that puts its stock back. Sending no items means everything still
     * outstanding on the order came back.
     */
    async receiveReturn(
      orderId: number,
      items?: Array<{
        product_id: number;
        quantity: number;
        damaged_quantity?: number;
      }>,
      remarks?: string,
    ) {
      return await postRequest<Order>(
        `order-manager/orders/${orderId}/receive-return`,
        { items, remarks },
      );
    },
    async getOrderByStage() {
      this.tableLoader = true;
      let url = `order-manager/orders/with-stage?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<OrderByStage[]>(url);
      if (!error) {
        const { data } = response;
        this.$state._orderStage = data;
        this.tableLoader = false;
      }
    },
    selectionChange(arr: Array<Order>) {
      this.multipleSelection = arr;
    },
    setInvoiceDialogVisibility(value: boolean) {
      this.showInvoiceDialog = value;
    },
    setInvoiceAutoPrint(value: boolean) {
      this.invoiceAutoPrint = value;
    },
    setSelectedOrderForInvoicePreview(order: Order | null) {
      this.$state._selectedOrderForInvoicePreview = order;
    },
    setSelectedOrdersForInvoicePreview(orders: Array<Order>) {
      this.$state._selectedOrdersForInvoicePreview = Array.isArray(orders)
        ? orders
        : [];
    },
    openInvoiceDialog(orders: Array<Order>, autoPrint = false) {
      this.setSelectedOrdersForInvoicePreview(orders);
      this.setSelectedOrderForInvoicePreview(orders[0] ?? null);
      this.setInvoiceAutoPrint(autoPrint);
      this.setInvoiceDialogVisibility(true);
    },
    closeInvoiceDialog() {
      this.setInvoiceDialogVisibility(false);
      this.setInvoiceAutoPrint(false);
      this.setSelectedOrderForInvoicePreview(null);
      this.setSelectedOrdersForInvoicePreview([]);
    },
    setFilterData(filter: any) {
      this.$state._filterData = filter;
    },
    setSubmittedFilterData(filter: any) {
      this.$state._submittedFilterData = filter;
    },
    setEditOrderData(order: Order) {
      this.$state._selectedOrderForEdit = order;
      this.$state._order = order;
    },
    setCreateModalVisibility(value: boolean) {
      this.showOrderCreateModal = value;
    },
    setOrderableData(data: null | Pick<Order, "source_id" | "source_type">) {
      this.$state._orderableData = data;
    },
    setSelectedProfileForOrder(profile: Profile) {
      this.$state._selectedProfileForOrder = profile;
    },
    toggleOrderCreateModal() {
      this.showOrderCreateModal = !this.showOrderCreateModal;
    },
    closeOrderCreateModal() {
      this.showOrderCreateModal = false;
    },
    openOrderEditModal() {
      this.showOrderEditModal = true;
    },
    closeOrderEditModal() {
      this.showOrderEditModal = false;
    },
    setBulkUploadModalVisibility(value: boolean) {
      this.showBulkUpload = value;
    },
    setView(view: OrderView) {
      this.$state._view = view;
    },
    relocateOrder(
      newStageGroupName: string,
      orderId: number,
      updatedStageId: number,
      updatedStageObject: OrderStage,
      newPosition: number,
    ) {
      let orderToMove = {} as Order;
      let currentArrayIndex = -1;
      this._orderStage.find((group, index) => {
        const orderIndex = group.orders.findIndex(
          (order) => order.id === orderId,
        );
        if (orderIndex !== -1) {
          orderToMove = group.orders.splice(orderIndex, 1)[0];
          currentArrayIndex = index;
        }
      });

      if (!orderToMove) {
        console.log(`Order with ID ${orderId} not found.`);
        return;
      }

      orderToMove.stage_id = updatedStageId;
      orderToMove.stage = updatedStageObject;
      orderToMove.position = newPosition;

      const targetArrayIndex = this._orderStage.findIndex(
        (group) => group.name === newStageGroupName,
      );

      if (targetArrayIndex !== -1) {
        this._orderStage[targetArrayIndex].orders.push(orderToMove);
      } else {
        this._orderStage.push({
          name: newStageGroupName,
          orders: [orderToMove],
        });
      }
    },
    // Website Integration APIs
    async getWebsiteIntegrations(type?: number) {
      let url = "order-manager/website-integrations";
      if (type) {
        url += `?type=${type}`;
      }
      const { response, error } = await getRequest(url);
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getWebsiteIntegration(id: number) {
      const { response, error } = await getRequest(
        `order-manager/website-integrations/${id}`,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async createWebsiteIntegration(data: any) {
      const response = await postRequest(
        "order-manager/website-integrations",
        data,
      );
      return response;
    },
    async updateWebsiteIntegration(id: number, data: any) {
      const response = await putRequest(
        `order-manager/website-integrations/${id}`,
        data,
      );
      return response;
    },
    async deleteWebsiteIntegration(id: number) {
      return await deleteRequest(`order-manager/website-integrations/${id}`);
    },
    async syncWebsiteIntegrationProducts(id: number) {
      const response = await postRequest(
        `order-manager/website-integrations/${id}/sync-products`,
        {},
      );
      return response;
    },
    async testWebsiteIntegrationConnection(id: number) {
      const response = await postRequest(
        `order-manager/website-integrations/${id}/test-connection`,
        {},
      );
      return response;
    },
    // Karbari Integration APIs
    async syncKarbariMetadata(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-metadata`,
        {},
      );
      return response;
    },
    async syncKarbariProducts(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-products`,
        {},
      );
      return response;
    },
    // EcomDrive Integration APIs
    async syncEcomDriveProducts(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-products`,
        {},
      );
      return response;
    },
    // Nuport Integration APIs
    async syncNuportMetadata(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-metadata`,
        {},
      );
      return response;
    },
    async syncNuportProducts(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-products`,
        {},
      );
      return response;
    },
    // BizMotion Integration APIs
    async syncBizMotionMetadata(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-metadata`,
        {},
      );
      return response;
    },
    async syncBizMotionProducts(integrationId: number) {
      const response = await postRequest(
        `order-manager/delivery-partners/${integrationId}/sync-products`,
        {},
      );
      return response;
    },
  },
});
